import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, CreditCard, Handshake, CheckCircle2, Clock } from "lucide-react";
import DebtLoanFormDialog from "@/components/DebtLoanFormDialog";
import AmortizationDialog from "@/components/AmortizationDialog";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { format, isPast, parseISO } from "date-fns";
import { Table2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * kind: "debt" | "loan"
 */
export default function DebtsLoansPage({ kind }) {
  const { t } = useTranslation();
  const isDebt = kind === "debt";
  const endpoint = isDebt ? "/debts" : "/loans";
  const title = isDebt ? t("debtsLoans.titleDebt") : t("debtsLoans.titleLoan");
  const subtitle = isDebt ? t("debtsLoans.subtitleDebt") : t("debtsLoans.subtitleLoan");
  const Icon = isDebt ? CreditCard : Handshake;
  const partyKey = isDebt ? "creditor" : "debtor";
  const partyLabel = isDebt ? t("debtsLoans.creditor") : t("debtsLoans.debtor");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [amortOpen, setAmortOpen] = useState(false);
  const [amortDebt, setAmortDebt] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(endpoint);
      setItems(res.data);
    } catch (e) { logger.error(`${kind} load`, e); }
    finally { setLoading(false); }
  }, [endpoint, kind]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [kind, load]);

  const handleNew = () => { setEditing(null); setDialogOpen(true); };
  const handleEdit = (it) => { setEditing(it); setDialogOpen(true); };
  const handleDelete = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Eliminado");
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2 flex items-center gap-2">
            <Icon className="w-3 h-3" /> {isDebt ? t("debtsLoans.kickerDebt") : t("debtsLoans.kickerLoan")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">{title}</h1>
          <p className="text-[#5F7368] mt-2">{subtitle}.</p>
        </div>
        <Button data-testid={`add-${kind}-btn`} onClick={handleNew} className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t("common.add")}
        </Button>
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-[#5F7368]">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <p className="text-[#8A9992] mb-4">Aún no hay registros.</p>
            <Button onClick={handleNew} className="bg-[#0F291E] hover:bg-[#1C7C54]">
              <Plus className="w-4 h-4 mr-2" /> Agregar el primero
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-[#EBEBEA]">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
              <div className="col-span-3">{partyLabel}</div>
              <div className="col-span-3">Descripción</div>
              <div className="col-span-2 text-right">Pendiente</div>
              <div className="col-span-1 text-right">Tasa</div>
              <div className="col-span-2">Vencimiento</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((it) => {
              let dueDate = null;
              if (it.due_date) {
                try { dueDate = parseISO(it.due_date); } catch (e) { logger.warn("parse due_date", e); }
              }
              const overdue = dueDate && isPast(dueDate) && it.status === "active";
              return (
                <div key={it.id} data-testid={`${kind}-row-${it.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-[#FAFAF9] transition-colors">
                  <div className="col-span-12 md:col-span-3">
                    <div className="font-medium text-[#0A110E] flex items-center gap-2">
                      {it[partyKey]}
                      {it.status === "paid" && <CheckCircle2 className="w-4 h-4 text-[#1C7C54]" />}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-3 text-sm text-[#5F7368] truncate">{it.description}</div>
                  <div className="col-span-6 md:col-span-2 md:text-right text-sm font-semibold tabular-nums" style={{ color: isDebt ? "#D95D39" : "#1C7C54" }}>
                    {formatCurrency(it.remaining_amount, it.currency)}
                  </div>
                  <div className="col-span-6 md:col-span-1 md:text-right text-sm tabular-nums text-[#5F7368]">
                    <div>{Number(it.interest_rate || 0).toFixed(2)}%</div>
                    {isDebt && it.rate_type && (
                      <div className="text-[10px] uppercase tracking-wider text-[#8A9992]">
                        {it.rate_type === "variable" ? "Variable" : "Fija"}
                      </div>
                    )}
                    {isDebt && it.monthly_payment ? (
                      <div className="text-xs text-[#0A110E] font-medium">
                        {formatCurrency(it.monthly_payment, it.currency)}/mes
                      </div>
                    ) : null}
                  </div>
                  <div className="col-span-6 md:col-span-2 text-sm">
                    {dueDate ? (
                      <div className={`flex items-center gap-1 ${overdue ? "text-[#D95D39]" : "text-[#5F7368]"}`}>
                        <Clock className="w-3 h-3" />
                        {format(dueDate, "dd MMM yyyy")}
                      </div>
                    ) : (
                      <span className="text-[#8A9992] text-xs">Sin fecha</span>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-1 flex justify-end gap-1">
                    {isDebt && (it.is_mortgage || it.term_months) && (
                      <button
                        onClick={() => { setAmortDebt(it); setAmortOpen(true); }}
                        data-testid={`amort-${it.id}`}
                        title="Ver amortización"
                        className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                      >
                        <Table2 className="w-4 h-4 text-[#1C7C54]" />
                      </button>
                    )}
                    <button onClick={() => handleEdit(it)} data-testid={`edit-${kind}-${it.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]"><Pencil className="w-4 h-4 text-[#5F7368]" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-${kind}-${it.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]"><Trash2 className="w-4 h-4 text-[#D95D39]" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(it.id)} className="bg-[#D95D39] hover:bg-[#D95D39]/90">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DebtLoanFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={load}
        kind={kind}
      />

      {isDebt && (
        <AmortizationDialog
          open={amortOpen}
          onOpenChange={setAmortOpen}
          debt={amortDebt}
        />
      )}
    </div>
  );
}
